/**
 * aminos-cart-client.js
 *
 * Single-writer queue for /cart/*.js mutations. All cart writes from custom
 * code (subscription toggle, cross-sell, quantity, remove) flow through this
 * module so we get serialized writes, abort-safe lifecycle, and one
 * authoritative refresh after every mutation.
 *
 * Why:
 *  - Concurrent writers (custom JS + Appstle + Klaviyo + Sections API) on the
 *    same /cart/*.js endpoints cause selling_plan corruption (silent MRR leak).
 *  - The live subscription-toggle.js had no debouncing, no AbortController, no
 *    state machine, and used line-by-index (position-dependent) lookups — all
 *    of which produced the race conditions we documented in the deepened plan.
 *
 * Usage:
 *   import { cartClient } from './aminos-cart-client.js';  // or via global
 *   const cart = await cartClient.change({ id: '<line_item_key>', quantity: 2 });
 *   const cart = await cartClient.swapSubscription({ key, variantId, sellingPlanId, subscribe: true });
 *
 * After every successful mutation the client refreshes the cart drawer DOM
 * via Section Rendering API and dispatches `aminos:cart:settled` for telemetry.
 *
 * Required:
 *   Appstle must be in "theme owns cart UI" mode (per Phase 2.2 spec) so its
 *   own JS doesn't race with this queue.
 */
(function (global) {
  'use strict';

  const STATES = Object.freeze({
    IDLE: 'idle',
    MUTATING: 'mutating',
    REFRESHING: 'refreshing',
    ERROR: 'error',
  });

  class CartClient {
    constructor() {
      this.state = STATES.IDLE;
      this.queue = [];
      this.activeAbort = null;
    }

    /** Public: enqueue a /cart/change.js mutation. */
    change(payload) {
      return this._enqueue(() => this._change(payload));
    }

    /** Public: enqueue a /cart/add.js mutation. */
    add(payload) {
      return this._enqueue(() => this._add(payload));
    }

    /** Public: enqueue an /cart/update.js mutation. */
    update(payload) {
      return this._enqueue(() => this._update(payload));
    }

    /**
     * Public: swap a line item to/from subscription.
     * Atomic where possible via /cart/change.js + selling_plan inline.
     * Falls back to remove-then-add with compensating undo on add-failure.
     */
    swapSubscription({ key, variantId, sellingPlanId, subscribe }) {
      return this._enqueue(async () => {
        // Capture quantity pre-mutation in case we need to compensate
        const cart = await this._fetchCart();
        const item = cart.items.find((i) => i.key === key);
        if (!item) throw new Error(`Line item key ${key} not found`);
        const originalQty = item.quantity;
        const originalSellingPlan = item.selling_plan_allocation
          ? item.selling_plan_allocation.selling_plan.id
          : null;

        // Try atomic path first: /cart/change.js with selling_plan inline.
        // Most subscription apps (including current Appstle) honor this.
        try {
          const body = {
            id: key,
            quantity: originalQty,
          };
          if (subscribe) {
            body.selling_plan = sellingPlanId;
          } else {
            body.selling_plan = '';
          }
          const res = await this._fetch('/cart/change.js', body);
          if (res.ok) return res.json();
          // Fall through to two-phase
          console.warn('[aminos-cart] Atomic selling_plan swap unsupported; falling back to two-phase');
        } catch (err) {
          if (err.name === 'AbortError') throw err;
          // Otherwise fall through
        }

        // Two-phase fallback: remove then add with compensating undo
        let removed = false;
        try {
          await this._fetch('/cart/change.js', { id: key, quantity: 0 });
          removed = true;
          const addBody = { id: variantId, quantity: originalQty };
          if (subscribe) addBody.selling_plan = sellingPlanId;
          const addRes = await this._fetch('/cart/add.js', addBody);
          if (!addRes.ok) throw new Error('Add failed after remove');
          return addRes.json();
        } catch (err) {
          if (removed) {
            // Compensating add to restore original state
            console.error('[aminos-cart] Add failed after remove — compensating');
            const compensateBody = { id: variantId, quantity: originalQty };
            if (originalSellingPlan) compensateBody.selling_plan = originalSellingPlan;
            try {
              await this._fetch('/cart/add.js', compensateBody);
              this._showToast('Couldn\'t switch subscription — your item is still in the cart.');
            } catch (compensateErr) {
              console.error('[aminos-cart] CRITICAL: compensating add also failed', { compensateErr, snapshot: { key, originalQty, originalSellingPlan } });
              this._showToast('Cart update failed. Please refresh the page.');
            }
          }
          throw err;
        }
      });
    }

    /** Public: read current cart state (does not mutate, but coalesces). */
    fetch() {
      return this._fetchCart();
    }

    /** Public: refresh cart drawer DOM only (no mutation). */
    refresh() {
      return this._refresh();
    }

    /** Public: current state machine state (for debugging / disabling controls). */
    getState() {
      return this.state;
    }

    /** Public: abort in-flight requests (call on controller disconnect). */
    abort() {
      if (this.activeAbort) {
        this.activeAbort.abort();
        this.activeAbort = null;
      }
      this.queue.length = 0;
      this.state = STATES.IDLE;
    }

    // ===== Internal =====

    _enqueue(taskFn) {
      return new Promise((resolve, reject) => {
        this.queue.push({ taskFn, resolve, reject });
        this._drain();
      });
    }

    async _drain() {
      if (this.state !== STATES.IDLE) return;
      const next = this.queue.shift();
      if (!next) return;
      this.state = STATES.MUTATING;
      this._dispatch('aminos:cart:mutating');
      try {
        const result = await next.taskFn();
        this.state = STATES.REFRESHING;
        await this._refresh();
        this.state = STATES.IDLE;
        next.resolve(result);
        this._dispatch('aminos:cart:settled', { cart: result });
      } catch (err) {
        this.state = err.name === 'AbortError' ? STATES.IDLE : STATES.ERROR;
        next.reject(err);
        this._dispatch('aminos:cart:error', { error: err });
        // Drain — recover by attempting next item
        if (this.state === STATES.ERROR) {
          this.state = STATES.IDLE;
        }
      } finally {
        // Continue draining
        if (this.queue.length) this._drain();
      }
    }

    async _change(payload) {
      const res = await this._fetch('/cart/change.js', payload);
      if (!res.ok) throw new Error(`change failed: ${res.status}`);
      return res.json();
    }

    async _add(payload) {
      const res = await this._fetch('/cart/add.js', payload);
      if (!res.ok) throw new Error(`add failed: ${res.status}`);
      return res.json();
    }

    async _update(payload) {
      const res = await this._fetch('/cart/update.js', payload);
      if (!res.ok) throw new Error(`update failed: ${res.status}`);
      return res.json();
    }

    async _fetch(url, body) {
      // Cancel any prior in-flight write — only one writer at a time
      if (this.activeAbort) this.activeAbort.abort();
      this.activeAbort = new AbortController();
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
        signal: this.activeAbort.signal,
      });
      this.activeAbort = null;
      return res;
    }

    async _fetchCart() {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`cart.js fetch failed: ${res.status}`);
      return res.json();
    }

    /**
     * Refresh the cart UI by driving Sleek 2.2's NATIVE `cartUpdate` pub/sub —
     * the exact pipeline a native quantity change uses (which is confirmed to
     * repaint correctly). We fetch the cart-drawer section + /cart.js, attach
     * the rendered HTML under `cart.sections[sectionId]`, and publish
     * `cartUpdate`. Sleek's CartItems subscriber then swaps
     * #CartDrawerBody / #CartDrawerFooter innerHTML (where our subscription
     * toggle and savings/subtotal live) from the server render.
     *
     * We deliberately do NOT patch innerHTML ourselves (left Sleek's
     * components holding stale state), and we drive the pub/sub directly
     * rather than via the `cart:refresh` event so we don't depend on
     * CartDrawer.onCartRefresh being bound. `cart:refresh` remains a fallback.
     */
    async _refresh() {
      const FoxTheme = global.FoxTheme;
      try {
        if (FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS && FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate) {
          const drawer = document.querySelector('cart-drawer[data-section-id]');
          const sectionId = (drawer && drawer.getAttribute('data-section-id')) || 'cart-drawer';
          const rootUrl = (FoxTheme.routes && FoxTheme.routes.root_url) || '/';
          const cartUrl = (FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';

          const [htmlRes, cartRes] = await Promise.all([
            fetch(`${rootUrl}?section_id=${sectionId}`, { credentials: 'same-origin' }),
            fetch(`${cartUrl}.js`, { credentials: 'same-origin' }),
          ]);
          const [html, cartData] = await Promise.all([htmlRes.text(), cartRes.json()]);

          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const sectionEl = parsed.getElementById(`CartDrawer-${sectionId}`);
          cartData.sections = {};
          if (sectionEl) cartData.sections[sectionId] = sectionEl.innerHTML;

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartData });
          this._dispatch('aminos:cart:dom-refreshed');
          return;
        }
      } catch (err) {
        console.error('[aminos-cart] native cartUpdate refresh failed; falling back to cart:refresh', err);
      }

      // Fallback: ask Sleek's <cart-drawer> to refresh itself. Resolve on the
      // theme's `cart:updated` event, or a short timeout so the queue is never
      // wedged by a missed event.
      await new Promise((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          document.removeEventListener('cart:updated', done);
          resolve();
        };
        document.addEventListener('cart:updated', done, { once: true });
        const timer = setTimeout(done, 1200);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
      this._dispatch('aminos:cart:dom-refreshed');
    }

    _dispatch(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    }

    _showToast(message) {
      // Hook into theme toast system; fallback to console for now
      const evt = new CustomEvent('aminos:toast', { bubbles: true, detail: { message } });
      document.dispatchEvent(evt);
      console.warn('[aminos-cart toast]', message);
    }
  }

  global.aminosCartClient = new CartClient();
})(window);
